/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ExternalLink, Copy, Check, Video,
  Image as ImageIcon, Music, Radio, FileQuestion,
  Calendar, HardDrive, FileText, Maximize2
} from 'lucide-react';
import Modal from './Modal';
import VideoPlayer from './VideoPlayer';
import AudioPlayer from './AudioPlayer';
import { formatFileSize } from '../utils/formatFileSize';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import toastService from '../utils/toastService';
import { SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS } from '../constants/serviceRequests';
import { SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const MEDIA_TYPE_ICONS = {
  video: Video,
  session_recording: Radio,
  audio: Music,
  image: ImageIcon,
};

const formatDate = (date) => {
  if (!date) { return '—'; }
  try {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const MediaViewerModal = ({ item, onClose }) => {
  const [copiedUid, setCopiedUid] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const uidTimeoutRef = useRef(null);
  const urlTimeoutRef = useRef(null);

  useEffect(() => {
    setPreviewFailed(false);
    setCopiedUid(false);
    setCopiedUrl(false);
    if (uidTimeoutRef.current) clearTimeout(uidTimeoutRef.current);
    if (urlTimeoutRef.current) clearTimeout(urlTimeoutRef.current);
  }, [item?.uid, item?.fileUrl]);

  const isVideoLike = item?.fileType === 'video' || item?.fileType === 'session_recording';
  const isAudio = item?.fileType === 'audio';
  const isImage = item?.fileType === 'image';
  const TypeIcon = (item?.fileType && MEDIA_TYPE_ICONS[item.fileType]) || FileQuestion;

  const handleCopy = async (text, type) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'uid') {
        if (uidTimeoutRef.current) clearTimeout(uidTimeoutRef.current);
        setCopiedUid(true);
        uidTimeoutRef.current = setTimeout(() => setCopiedUid(false), 2000);
      } else if (type === 'url') {
        if (urlTimeoutRef.current) clearTimeout(urlTimeoutRef.current);
        setCopiedUrl(true);
        urlTimeoutRef.current = setTimeout(() => setCopiedUrl(false), 2000);
      }
      toastService.success(`Copied ${type === 'uid' ? 'Media ID' : 'link'} to clipboard`);
    } catch {
      toastService.error('Failed to copy to clipboard');
    }
  };

  return (
    <Modal
      isOpen={!!item}
      onClose={onClose}
      title={item?.fileName || 'Media Item Details'}
      className="max-w-5xl w-full max-h-[90vh]"
    >
      {item && (
        <div className="flex flex-col md:flex-row gap-5 items-stretch min-h-0">
          {/* ---------------------------------------------------------------- */}
          {/* Left Column: Media Stage Viewport                                */}
          {/* ---------------------------------------------------------------- */}
          <div className="flex-1 min-w-0 bg-base-200/60 dark:bg-black/40 border border-base-content/10 overflow-hidden shadow-inner flex items-center justify-center relative min-h-[180px] md:min-h-[220px] h-[360px] md:h-[480px] max-h-[60vh] md:max-h-[68vh]">
            {isImage && !previewFailed && (
              <div className="relative group/preview w-full h-full flex items-center justify-center p-2">
                <img
                  src={item.fileUrl}
                  alt={item.fileName}
                  className="max-w-full max-h-full object-contain select-none"
                  onError={() => setPreviewFailed(true)}
                />
                <a
                  href={item.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-3 right-3 p-2 rounded-xl bg-base-100/90 hover:bg-base-100 text-base-content/70 hover:text-primary shadow-lg backdrop-blur-md opacity-0 group-hover/preview:opacity-100 transition-all text-xs font-bold flex items-center gap-1.5"
                  title="Open full image in new tab"
                >
                  <Maximize2 size={14} />
                  <span>Full size</span>
                </a>
              </div>
            )}

            {isVideoLike && (
              <div className="w-full h-full min-h-0 flex items-center justify-center">
                <VideoPlayer src={item.fileUrl} />
              </div>
            )}

            {isAudio && (
              <div className="w-full p-4">
                <AudioPlayer src={item.fileUrl} fileName={item.fileName} />
              </div>
            )}

            {((!isImage && !isVideoLike && !isAudio) || (isImage && previewFailed)) && (
              <div className="py-12 px-6 flex flex-col items-center justify-center text-center space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner">
                  <TypeIcon size={32} />
                </div>
                <div>
                  <p className="text-sm font-bold text-base-content">{item.fileName}</p>
                  <p className="text-xs text-base-content/50 mt-0.5">
                    {previewFailed ? 'Preview could not be loaded' : 'Preview not available'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Right Column: Details & Actions Panel                            */}
          {/* ---------------------------------------------------------------- */}
          <div className="md:w-72 lg:w-80 flex-shrink-0 flex flex-col justify-between gap-4 min-w-0">
            {/* Top Details & Action Group */}
            <div className="space-y-4">
              {/* Type Badge & File Info */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                    <TypeIcon size={12} />
                    <span>{SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[item.fileType] || item.fileType || 'Media'}</span>
                  </span>
                  <span className="text-xs font-bold text-base-content/60">
                    {formatFileSize(item.fileSize)}
                  </span>
                </div>
                <p className="text-sm font-bold text-base-content break-words line-clamp-2" title={item.fileName}>
                  {item.fileName}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                {item.serviceRequestUid && (
                  <button
                    type="button"
                    onClick={() => window.open(`/service-requests/${item.serviceRequestUid}`, '_blank', 'noopener,noreferrer')}
                    className="w-full flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-base-200 hover:bg-primary/10 text-base-content hover:text-primary border border-base-content/10 text-xs font-bold transition-all"
                  >
                    <FileText size={14} className="text-primary" />
                    <span className="truncate">{item.requestNumber ? `${SERVICE_REQUEST_LABEL.singular} #${item.requestNumber}` : `View ${SERVICE_REQUEST_LABEL.singular}`}</span>
                    <ExternalLink size={12} className="opacity-60 flex-shrink-0" />
                  </button>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={item.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-base-200 hover:bg-base-300 text-base-content/70 hover:text-base-content border border-base-content/10 text-xs font-bold transition-all text-center"
                    title="Open direct file link"
                  >
                    <ExternalLink size={13} />
                    <span>Direct Link</span>
                  </a>

                  <button
                    type="button"
                    onClick={() => handleCopy(item.fileUrl, 'url')}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-base-200 hover:bg-base-300 text-base-content/70 hover:text-base-content border border-base-content/10 text-xs font-bold transition-all"
                    title="Copy direct file URL"
                  >
                    {copiedUrl ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                    <span>{copiedUrl ? 'Copied' : 'Copy Link'}</span>
                  </button>
                </div>
              </div>

              {/* Metadata Details List */}
              <div className="space-y-2 pt-3 border-t border-base-content/10">
                <p className="text-[10px] font-black uppercase tracking-wider text-base-content/40">File Information</p>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between py-1 border-b border-base-content/5">
                    <span className="text-base-content/50 font-medium flex items-center gap-1.5">
                      <HardDrive size={13} className="text-base-content/40" /> Size
                    </span>
                    <span className="font-bold text-base-content">{formatFileSize(item.fileSize)}</span>
                  </div>

                  <div className="flex items-center justify-between py-1 border-b border-base-content/5">
                    <span className="text-base-content/50 font-medium flex items-center gap-1.5">
                      <Calendar size={13} className="text-base-content/40" /> Date Added
                    </span>
                    <div className="text-right">
                      <span className="font-bold text-base-content block" title={formatDate(item.createdAt)}>
                        {formatDate(item.createdAt)}
                      </span>
                      {item.createdAt && (
                        <span className="text-[10px] text-base-content/40 block font-medium">
                          {formatRelativeTime(item.createdAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  {item.requestNumber && (
                    <div className="flex items-center justify-between py-1 border-b border-base-content/5">
                      <span className="text-base-content/50 font-medium flex items-center gap-1.5">
                        <FileText size={13} className="text-base-content/40" /> {SERVICE_REQUEST_LABEL.singular}
                      </span>
                      <button
                        type="button"
                        onClick={() => window.open(`/service-requests/${item.serviceRequestUid}`, '_blank', 'noopener,noreferrer')}
                        className="font-bold text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <span>{item.requestNumber}</span>
                        <ExternalLink size={10} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom: Media UID */}
            {item.uid && (
              <div className="pt-2 border-t border-base-content/10">
                <div className="flex items-center justify-between gap-1 p-2 rounded-xl bg-base-200/50 border border-base-content/5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[9px] font-black uppercase tracking-wider text-base-content/40">Media ID</p>
                    <p className="font-mono text-[10px] text-base-content/60 truncate">{item.uid}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(item.uid, 'uid')}
                    className="p-1.5 text-base-content/50 hover:text-primary rounded-lg transition-colors flex-shrink-0"
                    title="Copy Media ID"
                  >
                    {copiedUid ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MediaViewerModal;

MediaViewerModal.propTypes = {
  item: PropTypes.shape({
    uid: PropTypes.string,
    fileType: PropTypes.string,
    fileUrl: PropTypes.string,
    fileName: PropTypes.string,
    fileSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    requestNumber: PropTypes.string,
    serviceRequestUid: PropTypes.string,
    accountUid: PropTypes.string,
    subAccountUid: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};


